// How an issue reads on screen: the status and origin chips, the pages line, the segments, the time.
// Shared by the queue's rows and the answer panel (mockups/issues-queue.html).
import type { SourceIssue, SourceIssueAnswer } from "@waypoint/contracts";
import { Icon } from "@waypoint/surface";
import { t, type PlainKey } from "../../i18n/t.js";
import { StatusChip } from "../../ui/form.js";
import {
  citedPages,
  holdsRender,
  isReopened,
  pageNumbers,
  provisionalPages,
} from "../../issues/model.js";

/** "Page 20" or "Pages 32, 41": the cited refs read as the guide's reader would say them. */
export function pagesLabel(refs: readonly string[]): string {
  const pages = refs.map(pageNumbers).join(", ");
  const several = refs.length > 1 || refs.some((r) => r.startsWith("pp."));
  return several ? t("issues.pages.many", { pages }) : t("issues.pages.one", { pages });
}

/** The printed-pages line for a row or the panel, with provisional pages marked. */
export function pagesText(issue: SourceIssue): string {
  const refs = citedPages(issue);
  if (refs.length === 0) return t("issues.pages.none");
  const label = pagesLabel(refs);
  return provisionalPages(issue).length > 0
    ? t("issues.pages.provisional", { pages: label })
    : label;
}

const GUIDE_NAMES: Readonly<Record<string, PlainKey>> = {
  dashboard: "issues.guide.dashboard",
  acat: "issues.guide.acat",
  caip: "issues.guide.caip",
  "acat-bps-us": "issues.guide.acat-bps-us",
};

/** A guide's name where the catalogue knows it; its id otherwise. */
export function guideName(id: string): string {
  const key = GUIDE_NAMES[id];
  return key === undefined ? id : t(key);
}

/** An ISO time as the reader would write it, in UTC so it reads the same wherever it is opened. */
export function formatWhen(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "UTC",
    hourCycle: "h23",
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${get("day")} ${get("month")} ${get("year")}, ${get("hour")}:${get("minute")} UTC`;
}

/** What an answer says, in words. */
export function answerText(answer: SourceIssueAnswer): string {
  switch (answer.form) {
    case "reading":
      return t("issues.answerText.reading", { page: pagesLabel([answer.page]) });
    case "confirm":
      return t("issues.answerText.confirm", { value: answer.value });
    case "text":
      return answer.text;
  }
}

export function StatusBadge({ issue }: { issue: SourceIssue }) {
  if (issue.status === "answered")
    return (
      <StatusChip tone="done">
        <Icon name="check" size={12} />
        {t("issues.status.answered")}
      </StatusChip>
    );
  if (issue.status === "waived")
    return (
      <StatusChip>
        <Icon name="close" size={12} />
        {t("issues.status.waived")}
      </StatusChip>
    );
  if (isReopened(issue))
    return (
      <StatusChip tone="stop">
        <Icon name="info" size={12} />
        {t("issues.status.reopened")}
      </StatusChip>
    );
  return (
    <StatusChip tone="open">
      <Icon name="loop" size={12} />
      {t("issues.status.open")}
    </StatusChip>
  );
}

export function OriginBadge({ issue }: { issue: SourceIssue }) {
  return issue.origin === "pipeline" ? (
    <StatusChip tone="done">
      <Icon name="eye" size={12} />
      {t("issues.origin.pipeline")}
    </StatusChip>
  ) : (
    <StatusChip>{t("issues.origin.breakdown")}</StatusChip>
  );
}

export function BlocksBadge({ issue }: { issue: SourceIssue }) {
  return holdsRender(issue) ? (
    <StatusChip tone="blocks">
      <Icon name="stop" size={12} />
      {t("issues.blocksRender")}
    </StatusChip>
  ) : null;
}

/** The kind, origin, status and blocks-render chips, in the order the mockup draws them. */
export function IssueChips({ issue }: { issue: SourceIssue }) {
  return (
    <>
      <StatusChip>{t(`issues.kind.${issue.kind}`)}</StatusChip>
      <OriginBadge issue={issue} />
      <StatusBadge issue={issue} />
      <BlocksBadge issue={issue} />
    </>
  );
}

/** A segment as "D4 · Title": the id in the mono chip, then its title where the catalogue has one. */
export function SegmentId({ id, title }: { id: string; title?: string | undefined }) {
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5">
      <span className="rounded-4 border border-wp-line-strong bg-wp-bg/58 px-1.5 py-px font-mono text-11 font-semibold text-wp-text-2">
        {id}
      </span>
      {title === undefined ? null : (
        <span className="text-12 text-wp-text-2">
          <span aria-hidden>· </span>
          {title}
        </span>
      )}
    </span>
  );
}

const PACK_NAMES: Readonly<Record<string, PlainKey>> = {
  "ops-console-dashboard": "issues.pack.ops-console-dashboard",
  "ops-console-acat": "issues.pack.ops-console-acat",
  "ops-console-caip": "issues.pack.ops-console-caip",
};

/** A pack's label where the catalogue of words knows it; its id otherwise. */
export function packName(id: string): string {
  const key = PACK_NAMES[id];
  return key === undefined ? id : t(key);
}
